import { useEffect, useRef, useState } from 'react';
import logger from 'ws-scripts/modules/logger';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import buildTrimJobs from '../utils/buildTrimJobs';
import { MAX_VEHICLE_IMAGE_JOBS } from '../utils/vehicleImagesConfig';

const VEHICLE_IMAGES_URL = '/api/widget/ws-incentives-search/vehicle-images';

const chunkJobs = (jobs, size) => {
	const batches = [];
	for (let i = 0; i < jobs.length; i += size) {
		batches.push(jobs.slice(i, i + size));
	}
	return batches;
};

/**
 * Client-side fetch for the deferred vehicle-image cohort.
 *
 * SSR caps the GV /rest/trims fan-out at SSR_VEHICLE_IMAGE_LIMIT vehicles
 * (see src/server/store.js). After hydration this hook computes the remaining
 * trim jobs — using the same buildTrimJobs logic as SSR — filters out vehicles
 * already present in ssrVehicles, and POSTs the residual job list to
 * /vehicle-images. The server fans out the GV calls and returns a byVehicleId
 * map in the same shape as vehicleDataLoaded.
 *
 * Returns the deferred byVehicleId map (empty until the POST resolves).
 * Widget.jsx merges this with ssrVehicles into the IncentivesContext value so
 * VehicleCard / DetailsView / showcase grid re-render as deferred data lands.
 *
 * No-op when: byVehicle is empty, every vehicle already has SSR data, or the
 * POST fails (failures degrade silently — those vehicles fall back to
 * <NoImage /> via the existing BR-IMG-003 chain).
 */
const useDeferredVehicleImages = ({
	byVehicle,
	mssShowcase,
	ssrVehicles,
	accountId,
	locale
}) => {
	const [deferredVehicles, setDeferredVehicles] = useState({});
	// One-shot per mount; deps cover the SSR-hydration race so the effect can
	// re-run if values arrive after the first render.
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current) return undefined;
		if (!accountId) return undefined;
		if (!byVehicle || Object.keys(byVehicle).length === 0) return undefined;

		const ssrIds = new Set(Object.keys(ssrVehicles || {}));
		const allJobs = buildTrimJobs(byVehicle, mssShowcase);
		const deferredJobs = allJobs.filter((job) => !ssrIds.has(job.gvId));

		if (deferredJobs.length === 0) return undefined;

		hasFiredRef.current = true;
		let cancelled = false;

		const batches = chunkJobs(deferredJobs, MAX_VEHICLE_IMAGE_JOBS);

		batches.forEach((batchJobs, batchIdx) => {
			fetch(VEHICLE_IMAGES_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					accountId,
					locale: locale || 'en_US',
					jobs: batchJobs
				})
			})
				.then((res) => {
					if (!res.ok) {
						throw new Error(`HTTP ${res.status}`);
					}
					return res.json();
				})
				.then((data) => {
					if (cancelled) return;
					const byVehicleId = data?.byVehicleId || {};
					setDeferredVehicles((prev) => ({
						...prev,
						...byVehicleId
					}));
				})
				.catch((err) => {
					if (cancelled) return;
					logger.warn(
						`incentives-search: deferred vehicle-images batch ${
							batchIdx + 1
						}/${batches.length} failed (non-fatal): ${err.message}`
					);
					setNewRelicCustomAttribute(
						'INCENTIVES_SEARCH_DEFERRED_IMAGES_ERROR',
						err.message
					);
				});
		});

		return () => {
			cancelled = true;
		};
	}, [accountId, byVehicle, ssrVehicles, mssShowcase, locale]);

	return deferredVehicles;
};

export default useDeferredVehicleImages;
